/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { APP_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import { API_BASE_URL, WS_BASE_URL } from '../../../api/config';
import { agentApi } from '../../../ai-platform/services/api';
import SubAgentEventsTree from '../../../ai-platform/components/chat/SubAgentEventsTree';
import AttachmentMessage from '../../../ai-platform/components/chat/AttachmentMessage';
import useVoiceChat from '../../../ai-platform/hooks/useVoiceChat';
import stripMarkdown from '../../../ai-platform/utils/stripMarkdown';
import {
  attachSubAgentEvent,
  flushPendingSubAgentEvents,
  isSubAgentEvent,
} from '../../../ai-platform/utils/subAgentEvents';
import { formatStoredTime } from '../../../utils/datetime';
import {
  extractDraftMutationPayload,
  getToolData,
} from './aiStrategyChatDraftMutations';

/**
 * AIStrategyChat — replaces the old one-shot AIStrategyGenerator with a
 * full conversational chat powered by the Bot Builder agent.
 *
 * Props:
 *  - botId: string — UUID of the bot being edited
 *  - botName: string — display name of the bot
 *  - botVersionId: string | undefined — UUID of the source version, when editing a published version copy
 *  - botDraftId: string | undefined — UUID of the current builder draft
 *  - currentSpec: the live builder state (entry_long/short, exit_long/short, risk, filters)
 *  - onApplyStrategy(spec): called when user accepts a generated spec
 *  - onCreateCheckpoint(label): called BEFORE applying so the parent can save a restore point
 *  - checkpoints: Array<{ id, label, timestamp }> — the list of saved checkpoints
 *  - onRestoreCheckpoint(id): called when user picks a checkpoint to restore
 *  - onDraftChanged(result): called when the agent mutated a builder draft via tools
 *  - onBotVersionChanged(): legacy callback for old version-mutating tools
 */

/* ------------------------------------------------------------------ */
/* Chat persistence helpers                                            */
/* ------------------------------------------------------------------ */

const chatStorageKey = (botId) => `ai-strategy-chat-${botId || 'unknown'}`;

const loadChatHistory = (botId) => {
  try {
    const raw = sessionStorage.getItem(chatStorageKey(botId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.messages)) return parsed;
    }
  } catch { /* ignore */ }
  return null;
};

const saveChatHistory = (botId, messages, sessionId) => {
  try {
    sessionStorage.setItem(chatStorageKey(botId), JSON.stringify({ messages, sessionId, ts: Date.now() }));
  } catch { /* ignore — quota etc */ }
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const getToken = () => localStorage.getItem('token');

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * Validate a sectioned spec structure before applying to the builder.
 * Returns { valid: boolean, errors: string[] }
 */
const validateSpec = (spec) => {
  const errors = [];
  const sp = spec?.strategy_params;
  const ss = sp?.sectioned_spec;

  if (!sp && !spec?.entry_long && !spec?.entry && !spec?.exit_long) {
    errors.push('Missing strategy_params or sectioned_spec');
    return { valid: false, errors };
  }

  const root = ss || spec;

  const sections = ['entry_long', 'entry_short', 'exit_long', 'exit_short'];
  for (const key of sections) {
    const section = root[key];
    if (section) {
      if (!section.op && !section.logic) {
        errors.push(`${key}: missing 'op' or 'logic' operator`);
      }
      if (section.clauses !== undefined && !Array.isArray(section.clauses)) {
        errors.push(`${key}: 'clauses' must be an array`);
      } else if (Array.isArray(section.clauses)) {
        for (let i = 0; i < section.clauses.length; i++) {
          const clause = section.clauses[i];
          if (clause.type === 'group') continue;
          const hasLhsOp = clause.lhs && clause.op;
          const hasIndicatorOp = clause.indicator && clause.operator;
          if (!hasLhsOp && !hasIndicatorOp && !clause.type) {
            errors.push(`${key}.clauses[${i}]: invalid clause structure`);
          }
        }
      }
    }
  }

  const risk = root.risk || sp?.risk;
  if (risk) {
    if (risk.risk_pct !== undefined && (risk.risk_pct < 0 || risk.risk_pct > 100)) {
      errors.push(`risk.risk_pct value ${risk.risk_pct} is out of range (0-100)`);
    }
    if (risk.max_open_positions !== undefined && risk.max_open_positions < 1) {
      errors.push('risk.max_open_positions must be >= 1');
    }
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Try to extract a sectioned_spec from agent message text.
 * Looks for fenced JSON blocks containing strategy_params.sectioned_spec
 * or a bare sectioned_spec object.
 */
const tryExtractSpec = (text) => {
  try {
    const jsonBlocks = [...text.matchAll(/```json\s*([\s\S]*?)```/gi)];
    for (const m of jsonBlocks) {
      const obj = JSON.parse(m[1]);
      if (obj?.strategy_params?.sectioned_spec) return obj;
      if (obj?.sectioned_spec) return { strategy_params: { sectioned_spec: obj.sectioned_spec, filters: obj.filters } };
      // Detect flat sectioned spec (agent may produce entry_long/exit_long at top level)
      if (obj?.entry_long || obj?.entry || obj?.exit_long) return { strategy_params: { sectioned_spec: obj } };
      // Also try inside strategy_params directly (agent may put entry_long there)
      if (obj?.strategy_params?.entry_long || obj?.strategy_params?.entry) {
        return { strategy_params: { sectioned_spec: obj.strategy_params, filters: obj.strategy_params?.filters } };
      }
    }
  } catch { /* ignore */ }
  return null;
};

/** Legacy tools that still mutate bot versions directly. */
const VERSION_MUTATION_TOOLS = new Set([
  'create_bot_version',
  'generate_strategy',
]);

const AIStrategyChat = ({
  botId,
  botName,
  botVersionId,
  botDraftId,
  botDraftStatus,
  currentSpec,
  onApplyStrategy,
  onCreateCheckpoint,
  checkpoints = [],
  onRestoreCheckpoint,
  onDraftChanged,
  onBotVersionChanged,
}) => {
  const strategyChatAccess = useActionAccess(APP_ACTION_SURFACES.strategyBuilderAiSend);
  /* ---- State ---- */
  const [agentId, setAgentId] = useState(null);
  const restoredChat = useMemo(() => loadChatHistory(botId), [botId]);
  const [messages, setMessages] = useState(restoredChat?.messages || []);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [sessionId, setSessionId] = useState(restoredChat?.sessionId || null);
  const [pendingSpec, setPendingSpec] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [showCheckpoints, setShowCheckpoints] = useState(false);

  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  // Buffer for nested sub-agent events that arrive before the matching
  // parent tool_call message has been committed to React state.
  const pendingSubAgentEventsRef = useRef({});
  const inputRef = useRef(null);
  const isFirstMessageRef = useRef(!restoredChat?.messages?.length);
  const draftMutatedRef = useRef(false);
  const latestDraftMutationRef = useRef(null);
  const versionMutatedRef = useRef(false);
  const newVersionIdRef = useRef(null); // Tracks version_id from create_bot_version tool result
  const prevVoiceMessageCountRef = useRef(restoredChat?.messages?.length || 0);

  /* ---- Persist chat to sessionStorage on changes ---- */
  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(botId, messages, sessionId);
    }
  }, [messages, sessionId, botId]);

  /* ---- Fetch Bot Builder agent id once ---- */
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/ai/expert/api/agent-system/agents/bot-builder/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setAgentId(data.id);
      } catch (err) {
        console.error('Failed to fetch Bot Builder agent', err);
      }
    };
    fetchAgent();
  }, []);

  /* ---- Auto-apply a spec to the builder (with checkpoint) ---- */
  const autoApplySpec = useCallback((spec) => {
    console.log('[AIStrategyChat] autoApplySpec called with:', JSON.stringify(spec, null, 2));
    const validation = validateSpec(spec);
    if (!validation.valid) {
      // Show as pending with errors — user must fix manually
      setPendingSpec(spec);
      setValidationErrors(validation.errors);
      toast.warning(`Strategy detected but has ${validation.errors.length} validation error(s) — review before applying`);
      return;
    }

    // Create checkpoint before applying
    if (onCreateCheckpoint) {
      const label = `Before AI update – ${formatStoredTime(new Date(), { hour: '2-digit', minute: '2-digit' }, '')}`;
      onCreateCheckpoint(label);
    }

    // Auto-apply
    if (onApplyStrategy) {
      onApplyStrategy(spec);
      toast.success('Strategy applied to builder automatically (checkpoint saved)');
    }

    setPendingSpec(null);
    setValidationErrors([]);
  }, [onApplyStrategy, onCreateCheckpoint]);

  const recordToolMutation = useCallback((toolName, result) => {
    const draftMutation = extractDraftMutationPayload(toolName, result);
    if (draftMutation) {
      draftMutatedRef.current = true;
      latestDraftMutationRef.current = draftMutation;
    }

    const data = getToolData(result);
    if (toolName === 'create_bot_version' && data.version_id) {
      newVersionIdRef.current = data.version_id;
    }
  }, []);

  const notifyMutationHandlers = useCallback(() => {
    if (draftMutatedRef.current && onDraftChanged) {
      onDraftChanged(latestDraftMutationRef.current || {
        toolName: 'unknown',
        draftId: botDraftId || null,
        data: {},
      });
    }
    if (versionMutatedRef.current && onBotVersionChanged) {
      onBotVersionChanged(newVersionIdRef.current);
    }

    draftMutatedRef.current = false;
    latestDraftMutationRef.current = null;
    versionMutatedRef.current = false;
    newVersionIdRef.current = null;
  }, [botDraftId, onBotVersionChanged, onDraftChanged]);

  /* ---- WebSocket lifecycle ---- */
  useEffect(() => {
    if (!agentId) return;

    const token = getToken();
    if (!token) return;

    const url = `${WS_BASE_URL}/ws/agents/${agentId}/chat/?token=${token}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);

        // Nested sub-agent events get attached to the matching parent tool_call.
        if (isSubAgentEvent(data)) {
          setMessages((prev) => {
            const updated = attachSubAgentEvent(prev, data);
            if (updated === prev) {
              const buf = pendingSubAgentEventsRef.current;
              if (!buf[data.parent_tool_call_id]) buf[data.parent_tool_call_id] = [];
              buf[data.parent_tool_call_id].push(data);
            }
            return updated;
          });
          return;
        }

        if (data.type === 'session_started' && data.session_id) {
          setSessionId(data.session_id);
        } else if (data.type === 'execution_started') {
          setExecutionId(data.execution_id || null);
          setExecuting(true);
          draftMutatedRef.current = false;
          latestDraftMutationRef.current = null;
          versionMutatedRef.current = false;
          newVersionIdRef.current = null;

        /* ---- Top-level tool_call event (sent directly, not inside execution_update) ---- */
        } else if (data.type === 'tool_call') {
          const toolName = data.tool_name;
          if (toolName) {
            setMessages((prev) => {
              const next = [...prev, {
                id: `tc-${data.tool_call_id || Date.now()}`,
                role: 'TOOL_CALL',
                toolName,
                toolArgs: data.tool_args || data.arguments,
                toolCallId: data.tool_call_id,
                subAgentEvents: [],
                ts: new Date(),
              }];
              return flushPendingSubAgentEvents(next, pendingSubAgentEventsRef.current);
            });
            if (VERSION_MUTATION_TOOLS.has(toolName)) {
              versionMutatedRef.current = true;
            }
          }

        /* ---- Top-level tool_result event (sent directly, not inside execution_update) ---- */
        } else if (data.type === 'tool_result') {
          const toolName = data.tool_name;
          const result = data.result;
          console.log(`[AIStrategyChat] tool_result for ${toolName}:`, JSON.stringify(result, null, 2));
          // Merge result into the matching tool_call message so the tree shows it.
          setMessages((prev) => {
            let idx = -1;
            if (data.tool_call_id) {
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].role === 'TOOL_CALL' && prev[i].toolCallId === data.tool_call_id) {
                  idx = i;
                  break;
                }
              }
            }
            if (idx === -1) {
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].role === 'TOOL_CALL' && prev[i].result == null) {
                  idx = i;
                  break;
                }
              }
            }
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = { ...updated[idx], result, success: result?.success };
            return updated;
          });
          recordToolMutation(toolName, result);
          if (result?.success && result?.data) {
            // generate_strategy returns the full strategy spec → auto-apply it
            if (toolName === 'generate_strategy') {
              const spec = result.data;
              // Wrap if needed
              const wrapped = spec?.strategy_params ? spec : { strategy_params: spec };
              autoApplySpec(wrapped);
            }
          }

        } else if (data.type === 'execution_completed') {
          if (data.result?.response) {
            const msg = { id: `a-${Date.now()}`, role: 'ASSISTANT', content: data.result.response, ts: new Date() };
            setMessages((prev) => [...prev, msg]);
            const spec = tryExtractSpec(data.result.response);
            if (spec) {
              autoApplySpec(spec);
            }
          }
          setExecuting(false);
          setExecutionId(null);
          notifyMutationHandlers();
        } else if (data.type === 'execution_update' && data.data) {
          const update = data.data;
          if (update.type === 'message' && update.message) {
            const msg = { id: String(update.message.id), role: update.message.role, content: update.message.content, ts: new Date(update.message.created_at) };
            setMessages((prev) => [...prev, msg]);
            const spec = tryExtractSpec(update.message.content);
            if (spec) {
              autoApplySpec(spec);
            }
          } else if (update.type === 'step' && update.step?.step_type === 'TOOL_CALL') {
            const toolName = update.step.tool_name;
            const stepId = update.step.id;
            setMessages((prev) => {
              // Skip if a top-level tool_call event already created the card.
              if (prev.some((m) => m.role === 'TOOL_CALL' && m.toolCallId && m.toolCallId === update.step.tool_call_id)) {
                return prev;
              }
              const next = [...prev, {
                id: `s-${stepId}`,
                role: 'TOOL_CALL',
                toolName,
                toolArgs: update.step.tool_input,
                toolCallId: update.step.tool_call_id || `step-${stepId}`,
                result: update.step.tool_output,
                success: update.step.tool_output?.success,
                subAgentEvents: [],
                ts: new Date(),
              }];
              return flushPendingSubAgentEvents(next, pendingSubAgentEventsRef.current);
            });
            if (VERSION_MUTATION_TOOLS.has(toolName)) {
              versionMutatedRef.current = true;
            }
            if (update.step.tool_output) {
              recordToolMutation(toolName, update.step.tool_output);
            }
          } else if (update.type === 'complete') {
            setExecuting(false);
            setExecutionId(null);
            notifyMutationHandlers();
          } else if (update.type === 'error') {
            setExecuting(false);
            setExecutionId(null);
            toast.error(update.error || 'Agent error');
          }
        } else if (data.type === 'execution_error') {
          toast.error(data.error || 'Execution error');
          setExecuting(false);
          setExecutionId(null);
        } else if (data.type === 'error') {
          toast.error(data.error || 'Error');
          setExecuting(false);
        }
      } catch (err) {
        console.error('WS parse error', err);
      }
    };

    ws.onerror = () => setConnected(false);
    ws.onclose = () => setConnected(false);

    return () => { if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [agentId, notifyMutationHandlers, autoApplySpec, recordToolMutation]);

  /* ---- Auto-scroll ---- */
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, executing]);

  /* ---- Build the context prefix for the first message ---- */
  const buildContextPrefix = useCallback(() => {
    const parts = [];
    if (botName || botId) {
      parts.push(`[Context: You are editing Bot "${botName || 'Untitled'}" (ID: ${botId || 'unknown'})`);
      if (botVersionId) {
        parts.push(`Source Version ID: ${botVersionId}`);
      }
      if (botDraftId) {
        parts.push(`Draft ID: ${botDraftId}`);
      }
      if (botDraftStatus) {
        parts.push(`Draft status: ${botDraftStatus}`);
      }
      parts.push('Use strategy draft tools for no-code builder edits. Create a draft only when no Draft ID exists. Only make changes to THIS bot. Do not create new bots or modify other bots.]');
    }
    return parts.length ? parts.join(', ') + '\n\n' : '';
  }, [botDraftId, botDraftStatus, botId, botName, botVersionId]);

  const buildExecutionContext = useCallback((extra = {}) => ({
    bot_id: botId || undefined,
    bot_name: botName || undefined,
    bot_version_id: botVersionId || undefined,
    bot_draft_id: botDraftId || undefined,
    bot_draft_status: botDraftStatus || undefined,
    current_spec: currentSpec || undefined,
    ...extra,
  }), [botDraftId, botDraftStatus, botId, botName, botVersionId, currentSpec]);

  const handleVoiceRecording = useCallback(async (file) => {
    if (!connected || executing || !wsRef.current) return;
    if (!strategyChatAccess.ensureAccess()) return;

    try {
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording');
      const visibleMessage = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      let messageToSend = visibleMessage;
      if (isFirstMessageRef.current) {
        messageToSend = buildContextPrefix() + visibleMessage;
        isFirstMessageRef.current = false;
      }

      setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'USER', content: visibleMessage, ts: new Date() }]);
      wsRef.current.send(JSON.stringify({
        type: 'execute',
        message: messageToSend,
        session_id: sessionId || undefined,
        attachment_ids: [attachment.id],
        context: buildExecutionContext({ attachment_ids: [attachment.id] }),
        max_iterations: 50,
      }));
      setExecuting(true);
    } catch (err) {
      console.error('Voice recording upload failed:', err);
      toast.error('Failed to send voice recording');
      throw err;
    }
  }, [buildContextPrefix, buildExecutionContext, connected, executing, sessionId, strategyChatAccess]);

  const {
    voiceState,
    isVoiceOn,
    interimTranscript,
    toggleVoice,
    speakResponse,
    isRecordingSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
  });

  useEffect(() => {
    if (messages.length > prevVoiceMessageCountRef.current) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.role === 'ASSISTANT' && lastMsg.content && isVoiceOn) {
        speakResponse(stripMarkdown(lastMsg.content));
      }
    }
    prevVoiceMessageCountRef.current = messages.length;
  }, [messages, isVoiceOn, speakResponse]);

  /* ---- Send message ---- */
  const sendMessage = useCallback(() => {
    if (!input.trim() || !connected || executing) return;
    if (!strategyChatAccess.ensureAccess()) return;

    // Prepend bot context on first message only
    let messageToSend = input;
    if (isFirstMessageRef.current) {
      const prefix = buildContextPrefix();
      messageToSend = prefix + input;
      isFirstMessageRef.current = false;
    }

    // Show the original input (without context prefix) in the UI
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'USER', content: input, ts: new Date() }]);

    wsRef.current?.send(JSON.stringify({
      type: 'execute',
      message: messageToSend,
      session_id: sessionId || undefined,
      context: buildExecutionContext(),
      max_iterations: 50,
    }));
    setExecuting(true);
    setInput('');
    inputRef.current?.focus();
  }, [input, connected, executing, sessionId, buildContextPrefix, buildExecutionContext, strategyChatAccess]);

  const stopExecution = () => {
    if (!executionId || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'stop', execution_id: executionId }));
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    setPendingSpec(null);
    setValidationErrors([]);
    isFirstMessageRef.current = true;
    try { sessionStorage.removeItem(chatStorageKey(botId)); } catch { /* ignore */ }
  };

  const handleApply = () => {
    if (!pendingSpec || !onApplyStrategy) return;

    // Create checkpoint before applying
    if (onCreateCheckpoint) {
      const label = `Before AI update – ${formatStoredTime(new Date(), { hour: '2-digit', minute: '2-digit' }, '')}`;
      onCreateCheckpoint(label);
    }
    onApplyStrategy(pendingSpec);
    setPendingSpec(null);
    setValidationErrors([]);
    toast.success('Strategy applied to builder (checkpoint saved)');
  };

  const shareCurrentSpec = () => {
    if (!connected || executing || !currentSpec) return;
    setInput(`Here is my current strategy spec for bot "${botName || 'this bot'}". Please review and suggest improvements:\n\`\`\`json\n${JSON.stringify(currentSpec, null, 2)}\n\`\`\``);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  /* ---- Connection status text ---- */
  const statusText = useMemo(() => {
    if (!agentId) return 'Loading agent…';
    if (!connected) return 'Connecting…';
    return 'Connected';
  }, [agentId, connected]);

  const statusColor = connected ? 'bg-green-500' : 'bg-orange-400';

  /* ================================================================ */
  /* Render                                                            */
  /* ================================================================ */

  return (
    <div className="rounded-xl border border-borderDefault bg-card overflow-hidden">
      {/* ---- Header ---- */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition"
      >
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" /></svg>
          <span className="font-semibold text-sm">AI Strategy Chat</span>
          {botName && <span className="text-xs text-textSecondary">— {botName}</span>}
          <span className={`inline-block w-2 h-2 rounded-full ${statusColor}`} title={statusText} />
          {messages.length > 0 && (
            <span className="text-xs text-textSecondary ml-1">({messages.filter(m => m.role !== 'SYSTEM' && m.role !== 'TOOL_CALL').length} messages)</span>
          )}
        </div>
        <svg className={`h-4 w-4 text-textSecondary transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {expanded && (
        <div className="border-t border-borderDefault">
          {/* ---- Toolbar ---- */}
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 border-b border-borderDefault text-xs">
            <button onClick={shareCurrentSpec} disabled={!connected || executing || !currentSpec}
              className="px-2 py-1 rounded border border-borderDefault hover:bg-muted disabled:opacity-40 transition">
              📋 Share current spec
            </button>
            <button onClick={clearChat}
              className="px-2 py-1 rounded border border-borderDefault hover:bg-muted transition">
              🗑 Clear chat
            </button>
            <div className="flex-1" />
            {/* Checkpoint dropdown — always visible */}
            <div className="relative">
              <button
                onClick={() => setShowCheckpoints((v) => !v)}
                className="px-2 py-1 rounded border border-borderDefault hover:bg-muted transition flex items-center gap-1"
              >
                🕐 Checkpoints ({checkpoints.length})
                <svg className={`h-3 w-3 transition-transform ${showCheckpoints ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showCheckpoints && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-card border border-borderDefault rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                  {checkpoints.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-textSecondary text-center">
                      No checkpoints yet. Checkpoints are created automatically when the AI applies changes.
                    </div>
                  ) : (
                    checkpoints.map((cp) => (
                      <button
                        key={cp.id}
                        onClick={() => { onRestoreCheckpoint?.(cp.id); setShowCheckpoints(false); toast.info(`Restored: ${cp.label}`); }}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-xs border-b border-borderDefault last:border-0 transition"
                      >
                        <div className="font-medium">{cp.label}</div>
                        <div className="text-textSecondary">{formatStoredTime(cp.timestamp, undefined, '-')}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---- Pending spec with validation errors (valid specs are auto-applied) ---- */}
          {pendingSpec && validationErrors.length > 0 && (
            <div className="px-3 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-yellow-600">⚠ Strategy spec detected but has validation errors</span>
                <Button
                  size="sm"
                  onClick={handleApply}
                  className="h-7 text-xs"
                  variant="outline"
                >
                  Apply Anyway
                </Button>
              </div>
              <div className="mt-2 space-y-1">
                <div className="text-xs font-medium text-red-500">Validation errors:</div>
                {validationErrors.map((err, i) => (
                  <div key={i} className="text-xs text-red-400 pl-2">• {err}</div>
                ))}
              </div>
            </div>
          )}

          {/* ---- Messages ---- */}
          <div className="h-72 overflow-y-auto px-3 py-3 space-y-3 bg-backgroundSurface/50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center text-textSecondary gap-2 py-8">
                <svg className="h-10 w-10 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" /></svg>
                <p className="text-sm font-medium">Describe your strategy{botName ? ` for "${botName}"` : ''}</p>
                <p className="text-xs">
                  Tell the AI what entries, exits, indicators, and risk rules you want. It will generate a spec you can apply to the builder.
                </p>
              </div>
            )}

            {messages.map((msg) => {
              if (msg.role === 'TOOL_CALL') {
                const hasNested = Array.isArray(msg.subAgentEvents) && msg.subAgentEvents.length > 0;
                return (
                  <div key={msg.id} className="flex gap-2">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold bg-gray-400">⚙</div>
                    <div className="flex-1 min-w-0 bg-muted/40 border border-borderDefault rounded-lg px-3 py-2 text-xs">
                      <div className="flex items-center gap-2 text-textSecondary">
                        <span>🔧</span>
                        <span className="font-medium text-textPrimary">{msg.toolName}</span>
                        {msg.success === true && <span className="text-green-600">✓</span>}
                        {msg.success === false && <span className="text-red-500">✗</span>}
                      </div>
                      {hasNested && (
                        <div className="mt-1">
                          <SubAgentEventsTree events={msg.subAgentEvents} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={msg.id}
                  className={`flex gap-2 ${msg.role === 'USER' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                    msg.role === 'USER' ? 'bg-primary' : msg.role === 'SYSTEM' ? 'bg-gray-400' : 'bg-green-600'
                  }`}>
                    {msg.role === 'USER' ? 'U' : msg.role === 'SYSTEM' ? '⚙' : 'AI'}
                  </div>
                  {/* Bubble */}
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'USER'
                      ? 'bg-primary text-white'
                      : msg.role === 'SYSTEM'
                      ? 'bg-muted text-textSecondary text-xs italic'
                      : 'bg-card border border-borderDefault text-textPrimary'
                  }`}>
                    {msg.role === 'SYSTEM' ? (
                      <span>{msg.content}</span>
                    ) : msg.role === 'USER' ? (
                      <AttachmentMessage content={msg.content} />
                    ) : (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_pre]:text-xs [&_pre]:p-2 [&_pre]:rounded [&_pre]:bg-black/5 [&_code]:text-xs [&_ul]:pl-4 [&_ol]:pl-4 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {executing && (
              <div className="flex gap-2">
                <div className="h-6 w-6 rounded-full bg-green-600 flex items-center justify-center text-white text-[10px] font-bold">AI</div>
                <div className="bg-card border border-borderDefault rounded-lg px-3 py-2 text-sm text-textSecondary flex items-center gap-2">
                  <span className="animate-pulse">●</span> Thinking…
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* ---- Input ---- */}
          <div className="border-t border-borderDefault px-3 py-2 bg-card">
            {!connected && agentId && (
              <div className="text-xs text-orange-500 mb-1">Reconnecting to agent…</div>
            )}
            {!agentId && (
              <div className="text-xs text-orange-500 mb-1">Bot Builder agent not found. Make sure it's provisioned.</div>
            )}
            {isVoiceOn && (
              <div className="text-xs text-textSecondary mb-1">
                {voiceState === 'recording' ? 'Recording voice message…' : voiceState === 'processing' ? 'Processing voice message…' : voiceState === 'speaking' ? 'Speaking response…' : interimTranscript}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                className="flex-1 resize-none rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-textSecondary"
                rows={2}
                placeholder={strategyChatAccess.disabled ? strategyChatAccess.blockedMessage : isVoiceOn ? 'Recording voice message…' : executing ? 'Agent is working…' : `Describe your strategy${botName ? ` for "${botName}"` : ''}…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!connected || executing || strategyChatAccess.disabled}
              />
              <div className="flex flex-col gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-full px-3 ${voiceState === 'recording' ? 'border-red-400 text-red-500' : ''}`}
                  onClick={toggleVoice}
                  disabled={!connected || executing || !isRecordingSupported || strategyChatAccess.disabled}
                  title={strategyChatAccess.disabled ? strategyChatAccess.blockedMessage : !isRecordingSupported ? 'Voice recording not supported' : voiceState === 'recording' ? 'Send voice recording' : 'Record voice'}
                >
                  {voiceState === 'recording' ? '🎙' : voiceState === 'processing' ? '…' : voiceState === 'speaking' ? '🔊' : '🎤'}
                </Button>
                {executing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-full border-red-400 text-red-500 hover:bg-red-50 px-3"
                    onClick={stopExecution}
                    title="Stop"
                  >
                    ■
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="h-full px-3"
                    onClick={sendMessage}
                    disabled={!input.trim() || !connected || strategyChatAccess.disabled}
                    title={strategyChatAccess.disabled ? strategyChatAccess.blockedMessage : 'Send'}
                  >
                    Send
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIStrategyChat;
